// pages/Agents/Workflows/WorkflowList.js
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, Divider, Alert, CircularProgress,
  Chip, IconButton, Switch, Tooltip, Paper, Grid, Card, CardContent,
  CardActions, Dialog, DialogTitle, DialogContent, DialogActions,
  Menu, MenuItem, ListItemIcon, ListItemText, TextField,
  InputAdornment, Snackbar, Stack, FormControl, InputLabel, Select,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import BoltIcon from "@mui/icons-material/Bolt";
import WorkflowGuide from "./WorkflowGuide";

const triggerIcons = {
  MANUAL: <TouchAppIcon fontSize="small" />,
  SCHEDULED: <ScheduleIcon fontSize="small" />,
  EVENT: <BoltIcon fontSize="small" />,
};
const triggerColors = { MANUAL: "default", SCHEDULED: "info", EVENT: "warning" };
const statusColors = { DRAFT: "default", ACTIVE: "success", INACTIVE: "error" };
const statusLabels = { DRAFT: "Draft", ACTIVE: "Active", INACTIVE: "Inactive" };
const modeIcons = {
  WORKFLOW: <AccountTreeIcon fontSize="small" />,
  AI_AGENT: <SmartToyIcon fontSize="small" />,
};

export default function WorkflowList() {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [runningId, setRunningId] = useState(null);
  const [showGuide, setShowGuide] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedWf, setSelectedWf] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchWorkflows = useCallback(async () => {
    if (!chatbotId) {
      setError("Chatbot ID is required.");
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axiosInstance.get("/api/workflows", { params: { chatbot_id: chatbotId } });
      setWorkflows(res.data.data || []);
    } catch {
      setError("Failed to load workflows.");
    } finally {
      setLoading(false);
    }
  }, [chatbotId]);

  useEffect(() => {
    document.title = "Agent Workflows | Klonit";
    fetchWorkflows();
  }, [fetchWorkflows]);

  const handleStatusToggle = async (wf) => {
    const newStatus = wf.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await axiosInstance.put(`/api/workflows/${wf.workflow_uid}`, { status: newStatus });
      setWorkflows((prev) =>
        prev.map((w) => (w.workflow_uid === wf.workflow_uid ? { ...w, status: newStatus } : w))
      );
      showSnackbar(`Workflow ${newStatus === "ACTIVE" ? "activated" : "deactivated"}`, "success");
    } catch {
      showSnackbar("Failed to update status.", "error");
    }
  };

  const handleToggleActiveMode = async (wf) => {
    const newMode = wf.active_mode === "AI_AGENT" ? "WORKFLOW" : "AI_AGENT";
    try {
      await axiosInstance.put(`/api/workflows/${wf.workflow_uid}`, { active_mode: newMode });
      setWorkflows((prev) =>
        prev.map((w) => (w.workflow_uid === wf.workflow_uid ? { ...w, active_mode: newMode } : w))
      );
      showSnackbar(`Switched to ${newMode === "AI_AGENT" ? "AI Agent" : "Pipeline"} mode`, "success");
    } catch {
      showSnackbar("Failed to switch mode.", "error");
    }
    handleMenuClose();
  };

  const handleRun = async (workflowUid) => {
    setRunningId(workflowUid);
    try {
      await axiosInstance.post(`/api/workflows/${workflowUid}/run`);
      showSnackbar("Workflow execution started", "success");
    } catch {
      showSnackbar("Failed to start workflow.", "error");
    } finally {
      setRunningId(null);
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      await axiosInstance.delete(`/api/workflows/${deleteDialog.workflow_uid}`);
      setWorkflows((prev) => prev.filter((w) => w.workflow_uid !== deleteDialog.workflow_uid));
      setDeleteDialog(null);
      showSnackbar("Workflow deleted", "success");
    } catch {
      showSnackbar("Failed to delete workflow.", "error");
    }
  };

  const handleMenuOpen = (e, wf) => {
    setMenuAnchor(e.currentTarget);
    setSelectedWf(wf);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedWf(null);
  };

  // Filter workflows
  const filteredWorkflows = workflows.filter((wf) => {
    const matchesSearch =
      !searchTerm ||
      wf.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (wf.description || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || wf.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            <AccountTreeIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Agent Workflows
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Automate tasks with sequential pipelines or AI-driven agents
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<HelpOutlineIcon />}
            onClick={() => setShowGuide(!showGuide)}
            size="small"
          >
            {showGuide ? "Hide Guide" : "How to Use"}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("create")}
          >
            New Workflow
          </Button>
        </Stack>
      </Box>

      {showGuide && <WorkflowGuide />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search workflows..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                {Object.entries(statusLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary" textAlign="right">
              {filteredWorkflows.length} workflow{filteredWorkflows.length !== 1 ? "s" : ""}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Workflow Grid */}
      {filteredWorkflows.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: "center" }}>
          <AccountTreeIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {workflows.length === 0 ? "No workflows yet" : "No workflows match your filters"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {workflows.length === 0
              ? "Create your first agent workflow to automate tasks like approving volunteers, triaging tickets, and more."
              : "Try adjusting your search or filters."}
          </Typography>
          {workflows.length === 0 && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("create")}>
              Create Workflow
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredWorkflows.map((wf) => (
            <Grid item xs={12} sm={6} md={4} lg={3.5} key={wf.workflow_uid}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "0.3s",
                  border: "1px solid",
                  borderColor: wf.status === "ACTIVE" ? "success.light" : "divider",
                  "&:hover": {
                    boxShadow: 6,
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  {/* Top row: status + menu */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      <Chip
                        label={statusLabels[wf.status] || wf.status}
                        color={statusColors[wf.status]}
                        size="small"
                      />
                      <Chip
                        icon={modeIcons[wf.active_mode] || modeIcons.WORKFLOW}
                        label={wf.active_mode === "AI_AGENT" ? "AI Agent" : "Pipeline"}
                        color={wf.active_mode === "AI_AGENT" ? "secondary" : "primary"}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, wf)}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Title */}
                  <Typography variant="subtitle1" fontWeight={700} noWrap gutterBottom>
                    {wf.name}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, height: 40, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                  >
                    {wf.description || "No description"}
                  </Typography>

                  <Divider sx={{ my: 1 }} />

                  {/* Stats row */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                    <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        Trigger
                      </Typography>
                      <Tooltip title={wf.trigger_type}>
                        <Chip
                          icon={triggerIcons[wf.trigger_type]}
                          label={wf.trigger_type}
                          color={triggerColors[wf.trigger_type]}
                          size="small"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      </Tooltip>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="caption" color="text.secondary">
                        Steps
                      </Typography>
                      <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                        {wf.steps ? wf.steps.length : 0}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography variant="caption" color="text.secondary">
                        Active
                      </Typography>
                      <Box>
                        <Switch
                          checked={wf.status === "ACTIVE"}
                          onChange={() => handleStatusToggle(wf)}
                          size="small"
                          color="success"
                        />
                      </Box>
                    </Box>
                  </Box>
                </CardContent>

                {/* Card Actions */}
                <CardActions sx={{ px: 2, pb: 2, pt: 0, gap: 0.5, flexWrap: "wrap" }}>
                  <Tooltip title="Run Workflow">
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={
                          runningId === wf.workflow_uid ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />
                        }
                        onClick={() => handleRun(wf.workflow_uid)}
                        disabled={runningId === wf.workflow_uid}
                        sx={{ minWidth: "auto" }}
                      >
                        Run
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`${wf.workflow_uid}/edit`)}
                    sx={{ minWidth: "auto" }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    startIcon={<HistoryIcon />}
                    onClick={() => navigate(`${wf.workflow_uid}/runs`)}
                    sx={{ minWidth: "auto" }}
                  >
                    Dashboard
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Context Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (selectedWf) navigate(`${selectedWf.workflow_uid}/edit`);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Workflow</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedWf) handleRun(selectedWf.workflow_uid);
          }}
        >
          <ListItemIcon>
            <PlayArrowIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Run Now</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (selectedWf) navigate(`${selectedWf.workflow_uid}/runs`);
          }}
        >
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Run Dashboard</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (selectedWf) handleToggleActiveMode(selectedWf);
          }}
        >
          <ListItemIcon>
            <SwapHorizIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            Switch to {selectedWf?.active_mode === "AI_AGENT" ? "Pipeline" : "AI Agent"}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedWf) handleStatusToggle(selectedWf);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            {selectedWf?.status === "ACTIVE" ? (
              <Chip label="Deactivate" size="small" color="warning" />
            ) : (
              <Chip label="Activate" size="small" color="success" />
            )}
          </ListItemIcon>
          <ListItemText>
            {selectedWf?.status === "ACTIVE" ? "Deactivate" : "Activate"}
          </ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (selectedWf) setDeleteDialog(selectedWf);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
        <DialogTitle>Delete Workflow</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "<strong>{deleteDialog?.name}</strong>"? This will
            also remove all run history. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
